import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Plus, FolderPlus, MoreHorizontal, Edit2, Trash2, Tag, Filter } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * FavoriteListsManager - Gestionnaire de listes de favoris
 * 
 * Composant pour créer, gérer et filtrer les listes personnalisées de favoris.
 * 
 * Props:
 * @param {Array} lists - Liste des FavoriteList de l'utilisateur
 * @param {string} selectedListId - ID de la liste actuellement sélectionnée (null = tous)
 * @param {Function} onListSelect - Callback quand une liste est sélectionnée
 * @param {Function} onListsChange - Callback quand les listes changent (création/suppression)
 * @param {Array} selectedFavoriteIds - IDs des favoris sélectionnés (pour ajout en batch)
 */
const FavoriteListsManager = ({ 
  lists = [], 
  selectedListId = null, 
  onListSelect,
  onListsChange,
  selectedFavoriteIds = []
}) => {
  
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [editingList, setEditingList] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Créer une nouvelle liste
  const handleCreateList = useCallback(async () => {
    if (!newListName.trim()) {
      toast.error('Veuillez entrer un nom pour la liste');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await api.post('/favorite-lists', { name: newListName.trim() });
      toast.success('Liste créée avec succès');
      setIsCreateDialogOpen(false);
      setNewListName('');
      
      if (onListsChange) {
        onListsChange();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [newListName, onListsChange]);

  // Renommer une liste
  const handleRenameList = useCallback(async () => {
    if (!editingList || !newListName.trim()) {
      toast.error('Veuillez entrer un nom pour la liste');
      return;
    }
    
    setIsLoading(true);
    try {
      await api.put(`/favorite-lists/${editingList.id}`, { name: newListName.trim() });
      toast.success('Liste renommée');
      setIsRenameDialogOpen(false);
      setEditingList(null);
      setNewListName('');
      
      if (onListsChange) {
        onListsChange();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors du renommage';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [editingList, newListName, onListsChange]);

  // Supprimer une liste
  const handleDeleteList = useCallback(async (listId) => {
    if (!window.confirm('Supprimer cette liste ? Les produits de votre Wishlist seront conservés.')) {
      return;
    }
    
    try {
      await api.delete(`/favorite-lists/${listId}`);
      toast.success('Liste supprimée');
      
      // Si la liste supprimée était sélectionnée, revenir à "Tous"
      if (selectedListId === listId && onListSelect) {
        onListSelect(null);
      }
      
      if (onListsChange) {
        onListsChange();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(message);
    }
  }, [selectedListId, onListSelect, onListsChange]);

  // Ajouter les favoris sélectionnés à une liste
  const handleAddToList = useCallback(async (listId) => {
    if (selectedFavoriteIds.length === 0) {
      toast.error('Aucun produit de la Wishlist sélectionné');
      return;
    }
    
    try {
      await api.post(`/favorite-lists/${listId}/items`, {
        favorite_ids: selectedFavoriteIds
      });
      toast.success(`${selectedFavoriteIds.length} produit(s) ajouté(s) à la liste`);
      
      if (onListsChange) {
        onListsChange();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'ajout';
      toast.error(message);
    }
  }, [selectedFavoriteIds, onListsChange]);

  // Ouvrir le dialog de renommage
  const openRenameDialog = (list) => {
    setEditingList(list);
    setNewListName(list.name);
    setIsRenameDialogOpen(true);
  };

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
      {/* Filtre par liste */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select 
          value={selectedListId || 'all'} 
          onValueChange={(value) => onListSelect && onListSelect(value === 'all' ? null : value)}
        >
          <SelectTrigger className="w-[200px]" data-testid="list-filter-select">
            <SelectValue placeholder="Filtrer par liste" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <div className="flex items-center gap-2">
                <span>Toute la Wishlist</span>
              </div>
            </SelectItem>
            {lists.map(list => (
              <SelectItem key={list.id} value={list.id}>
                <div className="flex items-center gap-2">
                  <Tag className="h-3 w-3" />
                  <span>{list.name}</span>
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {list.favorites_count || 0}
                  </Badge>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Bouton créer une liste */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" data-testid="create-list-btn">
            <FolderPlus className="h-4 w-4 mr-2" />
            Nouvelle liste
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer une liste</DialogTitle>
            <DialogDescription>
              Organisez votre Wishlist en créant des listes personnalisées.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              placeholder="Nom de la liste (ex: Noël 2025)"
              maxLength={50}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreateList()}
              data-testid="new-list-name-input"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Maximum 20 listes par compte
            </p>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsCreateDialogOpen(false)}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleCreateList} 
              disabled={isLoading || !newListName.trim()}
              data-testid="confirm-create-list-btn"
            >
              {isLoading ? 'Création...' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de renommage */}
      <Dialog open={isRenameDialogOpen} onOpenChange={setIsRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renommer la liste</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              placeholder="Nouveau nom"
              maxLength={50}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleRenameList()}
            />
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsRenameDialogOpen(false)}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleRenameList} 
              disabled={isLoading || !newListName.trim()}
            >
              {isLoading ? 'Renommage...' : 'Renommer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gestion des listes existantes */}
      {lists.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {lists.map(list => (
              <DropdownMenuItem 
                key={list.id}
                className="flex items-center justify-between"
              >
                <span className="truncate flex-1">{list.name}</span>
                <div className="flex items-center gap-1 ml-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      openRenameDialog(list);
                    }}
                  >
                    <Edit2 className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteList(list.id);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Bouton d'ajout rapide à une liste (si favoris sélectionnés) */}
      {selectedFavoriteIds.length > 0 && lists.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="default" size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter à ({selectedFavoriteIds.length})
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {lists.map(list => (
              <DropdownMenuItem 
                key={list.id}
                onClick={() => handleAddToList(list.id)}
              >
                <Tag className="h-4 w-4 mr-2" />
                {list.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

export default FavoriteListsManager;
